import VueWrapper from '@/components/core/Vue/vue.wrapper';
import {LOCATION_TYPES} from '@/globals';
import {LocationApi} from '@/sdk/api-services/location.api';
import {LocationPathNodeModel, LocationPathResultModel} from '@/sdk/models';
import {AdminDataFieldService} from '@/views/shared/services';
import {Component, Prop, Watch} from 'vue-property-decorator';
import WarningsListComponent from '../../../shared/warnings-list/warnings-list.component';
import EquipmentDbComponent from './equipment-db/equipment-db.component';
import IntrinsicSafetyParametersComponent from './intrinsic-safety-parameters/intrinsic-safety-parameters.component';

@Component({
    name: 'IsLoopComponent',
    components: {
        WarningsListComponent,
        EquipmentDbComponent,
        IntrinsicSafetyParametersComponent,
    },
})
export default class IsLoopComponent extends VueWrapper {
    public $refs!: {};

    @Prop({required: true}) public siteId!: string;

    public locationPathResult: LocationPathResultModel | null = null;
    public loading: boolean = false;
    public expandedPaths: Set<string> = new Set();
    public panel: number[] = [];

    public adminDataFieldService = new AdminDataFieldService();

    public selectedNode: LocationPathNodeModel | null = null;

    public mounted() {
        this.fetchLocationPaths();
    }

    @Watch('siteId')
    public onSiteIdChange() {
        this.fetchLocationPaths();
    }

    public async fetchLocationPaths() {
        if (!this.siteId) return;

        this.loading = true;
        try {
            const response = await new LocationApi().getLocationToLeafPathsAsync(this.siteId);

            if (response) {
                this.locationPathResult = response;
            } else {
                this.locationPathResult = null;
                this.AlertSrv.show('warning', 'No path data available.');
            }
        } catch (err: any) {
            this.AlertSrv.show('error', err.message ?? 'Unable to load location paths.');
            this.locationPathResult = null;
        } finally {
            this.loading = false;
        }
    }

    public togglePath(pathId: string) {
        if (this.expandedPaths.has(pathId)) {
            this.expandedPaths.delete(pathId);
        } else {
            this.expandedPaths.add(pathId);
        }
        this.$forceUpdate();
    }

    public isPathExpanded(pathId: string): boolean {
        return this.expandedPaths.has(pathId);
    }

    public getNodeTypeIcon(type: string | null | undefined): string {
        if (!type) return '/images/common/area-lg.png';
        switch (type) {
            case LOCATION_TYPES.DISTRIBUTION:
                return '/images/common/distribution-dark.png';
            case LOCATION_TYPES.JUNCTION_BOX:
                return '/images/jb.jpg';
            case LOCATION_TYPES.EQUIPMENT:
                return '/images/common/equipment.png';
            case LOCATION_TYPES.LOCATION:
                return '/images/common/area-lg.png';
            default:
                return '/images/common/area-lg.png';
        }
    }

    public getNodeTypeColor(type: string | null | undefined): string {
        if (!type) return 'grey';
        switch (type) {
            case LOCATION_TYPES.DISTRIBUTION:
                return 'primary';
            case LOCATION_TYPES.JUNCTION_BOX:
                return 'warning';
            case LOCATION_TYPES.EQUIPMENT:
                return 'success';
            case LOCATION_TYPES.LOCATION:
                return 'info';
            default:
                return 'grey';
        }
    }

    public expandAll() {
        if (this.locationPathResult?.paths) {
            this.locationPathResult.paths.forEach(path => {
                this.expandedPaths.add(path.pathId);
            });
            this.$forceUpdate();
        }
    }

    public collapseAll() {
        this.expandedPaths.clear();
        this.$forceUpdate();
    }

    public hasFailedRules(path: any): boolean {
        return path.nodes.some((node: LocationPathNodeModel) => node.isLoopRulesResults?.some(rule => !rule.passed));
    }

    public getFailedRules(node: LocationPathNodeModel): {ruleKey: string; passed: boolean; errorMsg: string}[] {
        if (!node.isLoopRulesResults) return [];
        return node.isLoopRulesResults.filter(rule => !rule.passed);
    }
}
